import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Loader2, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { updateIntradayFeedSettings } from "@/lib/api";

const hhmm = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }); } catch { return "—"; }
};
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const STATE_TONE = {
  on: "bg-emerald-500", outside_window: "bg-sky-500", off: "bg-slate-400", preview: "bg-slate-400", scheduler_down: "bg-red-500",
};
const STATE_LABEL = {
  on: "Scheduled pulls running", outside_window: "Scheduled pulls paused (outside hours)", off: "Scheduled pulls OFF",
  preview: "Preview — schedule disabled", scheduler_down: "Scheduled pulls NOT running",
};

export function ScheduleStatusLine({ schedule, testId = "bank-feed-schedule-state" }) {
  if (!schedule) return null;
  return (
    <div className="flex items-start gap-2" data-testid={testId} data-state={schedule.state}>
      <span className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${STATE_TONE[schedule.state] || "bg-slate-400"}`} />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900">{STATE_LABEL[schedule.state] || schedule.state}</div>
        <div className="text-xs text-slate-600">{schedule.reason}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex items-center justify-between gap-3 text-xs text-slate-700">
      <span>{label}</span>
      {children}
    </label>
  );
}

function RecentRuns({ runs }) {
  if (!runs?.length) return <p className="text-xs text-slate-500" data-testid="bank-feed-runs-empty">No pulls yet today.</p>;
  return (
    <ul className="space-y-1" data-testid="bank-feed-runs">
      {runs.map((r) => {
        const fresh = (r.accounts || []).reduce((n, a) => n + (a.new || 0), 0);
        return (
          <li key={r.run_id} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`bank-feed-run-${r.run_id}`}>
            <span className="font-mono text-slate-500">{hhmm(r.started_at)}</span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${r.trigger === "schedule" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>{r.trigger === "schedule" ? "scheduled" : `manual · ${r.by}`}</span>
            <span className="ml-auto text-slate-600">{fresh} new · {r.rows_today ?? 0} today</span>
            {r.errors?.length ? <span className="text-red-600" title={r.errors.join("\n")}>⚠</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

// Dean (Oct 2026): the 30-minute pull has to be visible and switchable from the page, not just an API call.
export function BankFeedSettings({ status, onSaved }) {
  const { isAdmin } = useAuth();
  const admin = isAdmin?.() ?? false;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const s = draft || status?.settings || {};
  const schedule = status?.schedule;

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await updateIntradayFeedSettings(patch);
      setDraft(null);
      toast.success(patch.enabled === false ? "Scheduled bank pulls switched off" : patch.enabled === true ? "Scheduled bank pulls switched on" : "Bank feed schedule saved");
      onSaved?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the bank feed settings");
    } finally { setSaving(false); }
  };
  const set = (k, v) => setDraft({ ...s, [k]: v });
  const dirty = !!draft;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDraft(null); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 px-2" title="Bank feed schedule & status" data-testid="bank-feed-settings-btn">
          <Settings2 className="w-4 h-4" />
          {schedule?.state === "scheduler_down" && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-red-500" data-testid="bank-feed-alert-dot" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[26rem] max-h-[85vh] space-y-4 overflow-y-auto text-sm" data-testid="bank-feed-settings">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Bank feed · GoCardless → Daily Collections</div>
          <div className="mt-1"><ScheduleStatusLine schedule={schedule} /></div>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-600">
            <span>Last scheduled pull</span><span className="font-mono text-right" data-testid="bank-feed-last-scheduled">{hhmm(schedule?.last_scheduled_run?.started_at)}</span>
            <span>Scheduler heartbeat</span><span className="font-mono text-right" data-testid="bank-feed-heartbeat">{schedule?.heartbeat_at ? `${hhmm(schedule.heartbeat_at)} (${schedule.heartbeat_age_min} min ago)` : "none"}</span>
            <span>Credits today</span><span className="font-mono text-right">{status?.rows_today ?? 0} · {status?.pending_today ?? 0} pending</span>
            <span>Manual pulls used today</span><span className="font-mono text-right">{status?.budget?.manual_used_today ?? 0} / {s.manual_pulls_per_day}</span>
          </div>
        </div>

        <div className="space-y-2 rounded-md border border-slate-200 p-3" data-testid="bank-feed-schedule-form">
          <Field label={<span className="font-semibold">Scheduled pulls</span>}>
            <Switch checked={s.enabled !== false} disabled={!admin || saving} onCheckedChange={(v) => save({ enabled: v })} data-testid="bank-feed-enabled-switch" />
          </Field>
          <Field label="Every">
            <Select value={String(s.every_minutes || 30)} onValueChange={(v) => set("every_minutes", Number(v))} disabled={!admin}>
              <SelectTrigger className="h-7 w-28 text-xs" data-testid="bank-feed-cadence"><SelectValue /></SelectTrigger>
              <SelectContent>{[15, 20, 30, 60].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Between (London, Mon–Fri)">
            <span className="flex items-center gap-1">
              <Select value={String(s.start_hour ?? 7)} onValueChange={(v) => set("start_hour", Number(v))} disabled={!admin}>
                <SelectTrigger className="h-7 w-20 text-xs" data-testid="bank-feed-start-hour"><SelectValue /></SelectTrigger>
                <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-slate-400">–</span>
              <Select value={String(s.end_hour ?? 19)} onValueChange={(v) => set("end_hour", Number(v))} disabled={!admin}>
                <SelectTrigger className="h-7 w-20 text-xs" data-testid="bank-feed-end-hour"><SelectValue /></SelectTrigger>
                <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
              </Select>
            </span>
          </Field>
          <Field label="Manual pulls per day">
            <Input type="number" min={0} max={20} value={s.manual_pulls_per_day ?? 3} onChange={(e) => set("manual_pulls_per_day", Number(e.target.value))} disabled={!admin} className="h-7 w-20 text-xs" data-testid="bank-feed-manual-cap" />
          </Field>
          <Field label="Mirror every credit to the Airtable tab">
            <Switch checked={s.airtable_mirror !== false} disabled={!admin || saving} onCheckedChange={(v) => save({ airtable_mirror: v })} data-testid="bank-feed-mirror-switch" />
          </Field>
          {status?.airtable_mirror && !status.airtable_mirror.ok && (
            <p className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800" data-testid="bank-feed-mirror-warning">
              ⚠ Airtable mirror failing — {status.airtable_mirror.failed} rows rejected on the last pull. {status.airtable_mirror.hint}.
            </p>
          )}
          {status?.airtable_mirror?.ok && (
            <p className="text-[11px] text-emerald-700" data-testid="bank-feed-mirror-ok">
              Airtable mirror OK — last pull wrote {status.airtable_mirror.created} new row{status.airtable_mirror.created === 1 ? "" : "s"}
              {status.airtable_mirror.linked_existing ? `, matched ${status.airtable_mirror.linked_existing} already in the tab` : ""}.
            </p>
          )}
          {admin ? (
            <div className="flex items-center justify-end gap-2 pt-1">
              {dirty && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setDraft(null)} data-testid="bank-feed-settings-cancel">Cancel</Button>}
              <Button size="sm" className="h-7 text-xs" disabled={!dirty || saving} data-testid="bank-feed-settings-save"
                onClick={() => save({ every_minutes: s.every_minutes, start_hour: s.start_hour, end_hour: s.end_hour, manual_pulls_per_day: s.manual_pulls_per_day })}>
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save schedule
              </Button>
            </div>
          ) : <p className="text-[11px] text-slate-500" data-testid="bank-feed-readonly-hint">Only Admins can change the schedule.</p>}
        </div>

        <div>
          <div className="mb-1 text-xs font-semibold text-slate-700">Bank accounts being pulled</div>
          {status?.connections?.length ? (
            <ul className="space-y-1.5" data-testid="bank-feed-connections">
              {status.connections.map((c) => (
                <li key={c.requisition_id} className="rounded border border-slate-200 p-2 text-[11px]" data-testid={`bank-feed-conn-${c.requisition_id}`} data-pulling={c.pulling.length > 0}>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-2 w-2 rounded-full ${c.pulling.length ? "bg-emerald-500" : "bg-red-500"}`} />
                    <span className="font-semibold text-slate-800">{c.bank}</span>
                    {c.pulling.length ? (
                      <span className="flex flex-wrap gap-1">{c.pulling.map((l, i) => <span key={`${l}-${i}`} className="rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] text-emerald-700">{l}</span>)}</span>
                    ) : <span className="text-red-700">not pulling</span>}
                    {c.linked && !c.expired && c.expires_at && <span className="ml-auto text-slate-400">consent to {new Date(c.expires_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>}
                  </div>
                  {c.accounts.some((a) => a.excluded) && (
                    <div className="mt-1 text-slate-500">Excluded: {c.accounts.filter((a) => a.excluded).map((a) => a.owner_name || a.label).join(", ")}</div>
                  )}
                  {c.issues.map((msg, i) => <div key={i} className="mt-1 text-red-700" data-testid="bank-feed-conn-issue">⚠ {msg}</div>)}
                </li>
              ))}
            </ul>
          ) : <p className="text-xs text-amber-700" data-testid="bank-feed-accounts-empty">No NatWest / Barclays consent at all — open Loan Repayments → Bank feeds and link SF1, SF2 and Barclays.</p>}
        </div>

        <div>
          <div className="mb-1 text-xs font-semibold text-slate-700">Recent pulls</div>
          <RecentRuns runs={status?.recent_runs} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
